import React, { useEffect, useState } from "react";
import {
  useCreatePackingDataMutation,
} from "./packingApi";
import { Col, Row } from "react-bootstrap";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import SelectBox from "../../../components/shared/fields/SelectBox ";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import Button from "../../../components/shared/buttons/button";
import useTranslator from "../../../hooks/useTranslator";
import { getCurrentDate } from "../../../helpers/Utils";
import toast from "react-hot-toast";

const PackedDataModal = ({ show, setShow, record, mode ,lot_number_id }) => {
  const { _t } = useTranslator();  
  const [createPackingData, { isLoading: isCreating }] =
    useCreatePackingDataMutation();

  const initialFormData = {
    lot_number_id: lot_number_id,
    pallets: "",
    boxes_packed: "",
    packed_before_lunch_type_one: "",
    packed_before_lunch_type_two: "",
    packed_after_lunch_type_one: "",
    packed_after_lunch_type_two: "",
    type: mode === "cheeks" ? "cheeks" : "strips",
  };
  
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    // console.log(mode);
    // console.log(record);
    if((mode === "editCheeks" || mode === "editStrips") && record !== null){
      setFormData(record);
    }else{
      setFormData(initialFormData);
    }

  }, [mode, record, show]);

  const handleClose = () => {
    setShow(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isCreating) return;

    const requiredFields = [
      {
        key: "packed_before_lunch_type_two",
        label:
          _t("packeddata_modal_packed_before_lunch", "Prime Packed Before Lunch") +
          " (20 pounds)",
      },
      {
        key: "packed_after_lunch_type_two",
        label:
          _t("packeddata_modal_packed_after_lunch", "Prime Packed After Lunch") +
          " (20)",
      },
    ];

    if (!isStrips) {
      requiredFields.push(
        {
          key: "packed_before_lunch_type_one",
          label:
            _t("packeddata_modal_packed_before_lunch", "Prime Packed Before Lunch") +
            " (10 pounds)",
        },
        {
          key: "packed_after_lunch_type_one",
          label:
            _t("packeddata_modal_packed_after_lunch", "Prime Packed After Lunch") +
            " (10 pounds)",
        }
      );
    }

    const emptyField = requiredFields.find(({ key }) => {
      const value = formData[key];
      return value === "" || value === null || value === undefined;
    });

    if (emptyField) {
      toast.error(
        _t(
          "all_packing_values_required",
          `Please enter ${emptyField.label} before submitting.`
        )
      );
      return;
    }

    const payload = { data_source: "packing", ...formData };

    if (isStrips) {
      delete payload.packed_before_lunch_type_one;
      delete payload.packed_after_lunch_type_one;
    }

    try {
      await createPackingData(payload).unwrap();
      handleClose();
    } catch (error) {
      console.error("Error creating/updating manager:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedFormData = {
      ...formData,
      [name]: value,
    };

    // Automatically update boxes_packed as the sum of packed_before_lunch and packed_after_lunch
    if (
      name === "packed_before_lunch_type_one" ||
      name === "packed_before_lunch_type_two" ||
      name === "packed_after_lunch_type_one" ||
      name === "packed_after_lunch_type_two"
    ) {
      const packedBeforeLunchTypeOne = isStrips ? 0 : parseInt(
        updatedFormData.packed_before_lunch_type_one || 0, 10
      );

      const packedBeforeLunchTypeTwo = parseInt(
        updatedFormData.packed_before_lunch_type_two || 0,
        10
      );

      const packedAfterLunchTypeOne = isStrips ? 0 : parseInt(
        updatedFormData.packed_after_lunch_type_one || 0, 10
      );

      const packedAfterLunchTypeTwo = parseInt(
        updatedFormData.packed_after_lunch_type_two || 0,
        10
      );

      const totalBoxesPacked =
        packedBeforeLunchTypeOne +
        packedBeforeLunchTypeTwo +
        packedAfterLunchTypeOne +
        packedAfterLunchTypeTwo;

      updatedFormData.boxes_packed = totalBoxesPacked.toString();

      // Calculate pallets based on boxes_packed / 80
      updatedFormData.pallets = Math.floor(totalBoxesPacked / 80); // Keep two decimal places
    }

    setFormData(updatedFormData);
  };

  const isStrips = mode === "strips" || mode === "editStrips";

  const renderFormFields = () => (
    <>
      <Col lg={6}>
        <SelectBox
          label={_t("packeddata_modal_type", "Type")}
          type="text"
          name="type"
          value={formData.type}
          options={[
            { value: "cheeks", label: _t("packeddata_modal_cheeks", "Cheeks") },
            { value: "strips", label: _t("packeddata_modal_strips", "Strips") },
          ]}
          onChange={handleChange}
          disabled={true}
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("packing_batch_code", "Packing Batch Code")}
          type="number"
          name="lot_number_id"
          value={formData.lot_number_id}
          onChange={handleChange}
          disabled={true}
          placeholder={"0"}
        />
      </Col>
      {!isStrips && (
        <Col lg={6}>
          <InputWithLabel
            label={
              _t("packeddata_modal_packed_before_lunch", "Prime Packed Before Lunch") + " (10 pounds)"
            }
            type="number"
            name="packed_before_lunch_type_one"
            value={formData.packed_before_lunch_type_one}
            onChange={handleChange}
            placeholder={"0"}
            required={true}
          />
        </Col>
      )}
      {!isStrips && (
        <Col lg={6}>
          <InputWithLabel
            label={
              _t("packeddata_modal_packed_after_lunch", "Prime Packed After Lunch") + " (10 pounds)"
            }
            type="number"
            name="packed_after_lunch_type_one"
            value={formData.packed_after_lunch_type_one}
            onChange={handleChange}
            placeholder={"0"}
            required={true}
          />
        </Col>
      )}
      <Col lg={6}>
        <InputWithLabel
          label={
            _t(
              "packeddata_modal_packed_before_lunch",
              "Prime Packed Before Lunch"
            ) + " (20 pounds)"
          }
          type="number"
          name="packed_before_lunch_type_two"
          value={formData.packed_before_lunch_type_two}
          onChange={handleChange}
          placeholder={"0"}
          required={true}
        />
      </Col>
      
      <Col lg={6}>
        <InputWithLabel
          label={
            _t(
              "packeddata_modal_packed_after_lunch",
              "Prime Packed After Lunch"
            ) + " (20)"
          }
          type="number"
          name="packed_after_lunch_type_two"
          value={formData.packed_after_lunch_type_two}
          onChange={handleChange}
          placeholder={"0"}
          required={true}
        />
      </Col>

      <Col lg={6}>
        <InputWithLabel
          label={`Pallets ${
            mode === "cheeks"
              ? _t("packeddata_modal_cheeks", "Cheeks")
              : _t("packeddata_modal_strips", "Strips")
          }`}
          type="number"
          name="pallets"
          value={formData.pallets}
          disabled={true} // Disabled to prevent manual entry
          placeholder={"0"}
        />
      </Col>
    </>
  );

  
  const modeLabels = {
    cheeks: _t("add_cheeks_packing", "Add Cheeks Packing"),
    strips: _t("add_strips_packing", "Add Strips Packing"),
    editCheeks: _t("edit_cheeks_packing", "Edit Cheeks Packing"),
    editStrips: _t("edit_strips_packing", "Edit Strips Packing"),
  };

  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={modeLabels[mode]}
      children={formData}
    >
      <Row>{renderFormFields()}</Row>

      <Row>
        <Col className="text-end">
          <Button
            label={_t("packeddata_modal_save_changes", "Save Changes")}
            size="small"
            onClick={handleSubmit}
            disabled={isCreating}
          />
        </Col>
      </Row>
    </CommonModal>
  );
};

export default PackedDataModal;
